/**
 * Persona test cases for cross-suite test fixtures.
 *
 * Phase 2.1: Defines canonical persona cases used by web unit tests,
 * API integration tests, and Playwright E2E tests. Each persona encodes
 * a realistic combination of role tier, permissions, enabled modules,
 * AAL level, and expected route visibility.
 *
 * These are pure data — no side effects. Test suites import and adapt
 * them into their respective fixture formats (render props, API auth
 * overrides, Playwright storage states).
 *
 * FIX: Uses single route-state map instead of overlapping arrays.
 * Each route has exactly one expected state: AVAILABLE, DISABLED, or HIDDEN.
 *
 * P0-1: Personas now reference bundle IDs only; effective permissions
 * resolved via PERMISSION_BUNDLE_REGISTRY in tests.
 * P0-2: Platform personas no longer receive branch business permissions.
 * P0-3: Support delegate uses DelegationContext, not ORG_ADMIN tier.
 * P1-6: Uses NavigationAvailability directly (not collapsed DISABLED).
 */
import { ROUTE_IDS } from '../routes/ids.js';
import { PLATFORM_ROOT_ADMIN_BASELINE_MODULES } from '../../access/personas/presets.js';
const COMMON_VISIBLE_ROUTES = [ROUTE_IDS.DASHBOARD, ROUTE_IDS.SETTINGS];
const COMMON_HIDDEN_ROUTES = [
    ROUTE_IDS.LOGIN,
    ROUTE_IDS.FORGOT_PASSWORD,
    ROUTE_IDS.RESET_PASSWORD,
    ROUTE_IDS.MFA_VERIFY,
    ROUTE_IDS.MFA_ENROLL,
];
const ORG_ADMIN_ONLY_ROUTES = [
    ROUTE_IDS.ORGANIZATION_GOVERNANCE_WORKSPACE,
    ROUTE_IDS.ADMIN_OPENING_CUTOVER_LIST,
    ROUTE_IDS.ADMIN_OPENING_CUTOVER_HISTORY,
    ROUTE_IDS.ADMIN_TAX_RULES_LIST,
    ROUTE_IDS.ADMIN_FEE_RULES_LIST,
    ROUTE_IDS.TRANSACTION_APPROVALS,
    ROUTE_IDS.TRANSACTION_REVERSALS,
    ROUTE_IDS.ORGANIZATION_JURISDICTION_SETTINGS,
];
function makeRouteExpectations(visible, disabled = [], hidden = []) {
    const expectations = {};
    for (const route of visible) {
        expectations[route] = { state: 'AVAILABLE' };
    }
    for (const route of disabled) {
        if (expectations[route]) {
            throw new Error(`Route ${route} defined in both visible and disabled`);
        }
        expectations[route] = { state: 'LOCKED_PERMISSION', reasonCode: 'PERMISSION_DENIED' };
    }
    for (const route of hidden) {
        if (expectations[route]) {
            throw new Error(`Route ${route} defined in multiple expectation categories`);
        }
        expectations[route] = { state: 'HIDDEN' };
    }
    for (const route of ORG_ADMIN_ONLY_ROUTES) {
        if (!expectations[route])
            expectations[route] = { state: 'HIDDEN' };
    }
    return expectations;
}
// P0-3: Support delegate uses delegation context, not ORG_ADMIN tier
const SUPPORT_DELEGATE_PERMISSIONS = [
    'data:hub',
    'organization:dashboard:read',
    'organization:transactions:list',
    'organization:transactions:read',
    'organization:instruments:read',
    'organization:holdings:read',
    'organization:facts:read',
    'organization:reconciliation:list',
    'organization:reconciliation:read',
    'organization:reports:list',
    'organization:reports:read',
    'organization:corporate-actions:read',
    'settings:view',
    'settings:profile',
    'settings:preferences',
    'navigation:read',
    'auth:profile:read',
    'auth:context',
    'auth:switchContext',
];
const ALL_MODULES = [
    'INVESTMENT',
    'ACCOUNTING',
    'TAX',
    'RECONCILIATION',
    'CORPORATE_ACTIONS',
    'REPORTING',
    'AUTH',
    'DASHBOARD',
    'SETTINGS',
    'ADMIN',
    'PLATFORM',
    'NAVIGATION',
    'DATA',
    'INSTRUMENTS',
    'IMPORT',
    'FACTS',
    'MARGIN_TRADING',
    'ORDER_MANAGEMENT',
];
export const PERSONA_CASES = [
    {
        id: 'platform-root',
        label: 'Platform Root',
        tier: 'PLATFORM_ROOT',
        aal: 'AAL2',
        bundleIds: ['platform.root'],
        enabledModules: [...PLATFORM_ROOT_ADMIN_BASELINE_MODULES],
        actorType: 'USER',
        expectedRoutes: makeRouteExpectations([
            ...COMMON_VISIBLE_ROUTES,
            ROUTE_IDS.REPORTS,
            ROUTE_IDS.ADMIN_HUB,
            ROUTE_IDS.DATA_HUB,
            ROUTE_IDS.PLATFORM_ORGANIZATIONS_LIST,
            ROUTE_IDS.PLATFORM_USERS_LIST,
            ROUTE_IDS.PLATFORM_AUDIT_LIST,
            ROUTE_IDS.PLATFORM_HEALTH,
            ROUTE_IDS.PLATFORM_MODULES_LIST,
            ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
            ROUTE_IDS.PLATFORM_ACCESS_INSPECTOR,
            ROUTE_IDS.SETTINGS_PROFILE,
            ROUTE_IDS.SETTINGS_PREFERENCES,
            ROUTE_IDS.SETTINGS_MFA,
            ROUTE_IDS.DATA_INSTRUMENTS,
            ROUTE_IDS.DATA_INSTRUMENT_DETAIL,
            ROUTE_IDS.DATA_MARKET_PRICES,
        ], [
            ROUTE_IDS.TRANSACTIONS,
            ROUTE_IDS.TRANSACTION_CREATE,
            ROUTE_IDS.TRANSACTION_APPROVALS,
            ROUTE_IDS.TRANSACTION_REVERSALS,
            ROUTE_IDS.DATA_IMPORTS,
            ROUTE_IDS.DATA_CORPORATE_ACTIONS,
            ROUTE_IDS.DATA_RECONCILIATION,
            ROUTE_IDS.DATA_FACT_BATCHES,
            ROUTE_IDS.DATA_HOLDINGS,
            ROUTE_IDS.DATA_CASH,
            ROUTE_IDS.DATA_POSITION_TRANSFERS,
            ROUTE_IDS.DATA_MANAGEMENT_FEES,
            ROUTE_IDS.DATA_PERFORMANCE_FEES,
            ROUTE_IDS.DATA_SETTLEMENT_INSTRUCTIONS,
            ROUTE_IDS.DATA_SETTLEMENT_FAILURES,
            ROUTE_IDS.HOLDINGS,
            ROUTE_IDS.ORGANIZATION_USERS_LIST,
            ROUTE_IDS.ORGANIZATION_ROLES_LIST,
            ROUTE_IDS.ORGANIZATION_PROFILE,
            ROUTE_IDS.ORGANIZATION_AUDIT_LIST,
        ], [
            ...COMMON_HIDDEN_ROUTES,
            ROUTE_IDS.ORGANIZATION_ACCESS_INSPECTOR,
            ROUTE_IDS.ORGANIZATION_BRANCHES_LIST,
            ROUTE_IDS.ORGANIZATION_MODULES_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENTS_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
            ROUTE_IDS.ORGANIZATION_PORTFOLIOS_LIST,
            ROUTE_IDS.DATA_CLIENTS,
            ROUTE_IDS.DATA_PORTFOLIOS,
            ROUTE_IDS.DATA_POSITIONS,
            ROUTE_IDS.DATA_JOURNALS,
            ROUTE_IDS.DATA_FISCAL_PERIODS,
            ROUTE_IDS.DATA_PERFORMANCE,
            ROUTE_IDS.DATA_TAX_RECORDS,
            ROUTE_IDS.DATA_RISK_EXPOSURES,
            ROUTE_IDS.DATA_COMPLIANCE_BREACHES,
        ]),
        description: 'Platform root administrator with full system access including license management. No organization business access.',
    },
    {
        id: 'platform-admin',
        label: 'Platform Admin',
        tier: 'PLATFORM_ADMIN',
        aal: 'AAL2',
        bundleIds: ['platform.admin'],
        enabledModules: [...PLATFORM_ROOT_ADMIN_BASELINE_MODULES],
        actorType: 'USER',
        expectedRoutes: makeRouteExpectations([
            ...COMMON_VISIBLE_ROUTES,
            ROUTE_IDS.REPORTS,
            ROUTE_IDS.ADMIN_HUB,
            ROUTE_IDS.DATA_HUB,
            ROUTE_IDS.PLATFORM_ORGANIZATIONS_LIST,
            ROUTE_IDS.PLATFORM_USERS_LIST,
            ROUTE_IDS.PLATFORM_AUDIT_LIST,
            ROUTE_IDS.PLATFORM_HEALTH,
            ROUTE_IDS.PLATFORM_MODULES_LIST,
            ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
            ROUTE_IDS.SETTINGS_PROFILE,
            ROUTE_IDS.SETTINGS_PREFERENCES,
            ROUTE_IDS.SETTINGS_MFA,
            ROUTE_IDS.DATA_INSTRUMENTS,
            ROUTE_IDS.DATA_INSTRUMENT_DETAIL,
            ROUTE_IDS.DATA_MARKET_PRICES,
            ROUTE_IDS.PLATFORM_ACCESS_INSPECTOR,
        ], [
            ROUTE_IDS.TRANSACTIONS,
            ROUTE_IDS.TRANSACTION_CREATE,
            ROUTE_IDS.TRANSACTION_APPROVALS,
            ROUTE_IDS.TRANSACTION_REVERSALS,
            ROUTE_IDS.DATA_IMPORTS,
            ROUTE_IDS.DATA_CORPORATE_ACTIONS,
            ROUTE_IDS.DATA_RECONCILIATION,
            ROUTE_IDS.DATA_FACT_BATCHES,
            ROUTE_IDS.DATA_HOLDINGS,
            ROUTE_IDS.DATA_CASH,
            ROUTE_IDS.DATA_POSITION_TRANSFERS,
            ROUTE_IDS.DATA_MANAGEMENT_FEES,
            ROUTE_IDS.DATA_PERFORMANCE_FEES,
            ROUTE_IDS.DATA_SETTLEMENT_INSTRUCTIONS,
            ROUTE_IDS.DATA_SETTLEMENT_FAILURES,
            ROUTE_IDS.HOLDINGS,
            ROUTE_IDS.ORGANIZATION_USERS_LIST,
            ROUTE_IDS.ORGANIZATION_ROLES_LIST,
            ROUTE_IDS.ORGANIZATION_PROFILE,
            ROUTE_IDS.ORGANIZATION_AUDIT_LIST,
        ], [
            ...COMMON_HIDDEN_ROUTES,
            ROUTE_IDS.ORGANIZATION_ACCESS_INSPECTOR,
            ROUTE_IDS.ORGANIZATION_BRANCHES_LIST,
            ROUTE_IDS.ORGANIZATION_MODULES_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENTS_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
            ROUTE_IDS.ORGANIZATION_PORTFOLIOS_LIST,
            ROUTE_IDS.DATA_CLIENTS,
            ROUTE_IDS.DATA_PORTFOLIOS,
            ROUTE_IDS.DATA_POSITIONS,
            ROUTE_IDS.DATA_JOURNALS,
            ROUTE_IDS.DATA_FISCAL_PERIODS,
            ROUTE_IDS.DATA_PERFORMANCE,
            ROUTE_IDS.DATA_TAX_RECORDS,
            ROUTE_IDS.DATA_RISK_EXPOSURES,
            ROUTE_IDS.DATA_COMPLIANCE_BREACHES,
        ]),
        description: 'Platform administrator with platform-scope permissions only. No organization business module access.',
    },
    {
        id: 'platform-user',
        label: 'Platform User',
        tier: 'PLATFORM_USER',
        aal: 'AAL1',
        bundleIds: ['platform.user'],
        enabledModules: ['DASHBOARD', 'SETTINGS', 'PLATFORM', 'AUTH', 'REPORTING'],
        actorType: 'USER',
        expectedRoutes: makeRouteExpectations([
            ...COMMON_VISIBLE_ROUTES,
            ROUTE_IDS.SETTINGS_PROFILE,
            ROUTE_IDS.SETTINGS_PREFERENCES,
            ROUTE_IDS.REPORTS,
            ROUTE_IDS.PLATFORM_HEALTH,
        ], [
            ROUTE_IDS.HOLDINGS,
            ROUTE_IDS.RECONCILIATION,
            ROUTE_IDS.CORPORATE_ACTIONS,
            ROUTE_IDS.DATA_HUB,
            ROUTE_IDS.ADMIN_HUB,
        ], [
            ...COMMON_HIDDEN_ROUTES,
            ROUTE_IDS.DATA_INSTRUMENTS,
            ROUTE_IDS.DATA_MARKET_PRICES,
            ROUTE_IDS.DATA_IMPORTS,
            ROUTE_IDS.DATA_CORPORATE_ACTIONS,
            ROUTE_IDS.DATA_RECONCILIATION,
            ROUTE_IDS.DATA_FACT_BATCHES,
            ROUTE_IDS.DATA_HOLDINGS,
            ROUTE_IDS.DATA_CASH,
            ROUTE_IDS.DATA_POSITION_TRANSFERS,
            ROUTE_IDS.DATA_MANAGEMENT_FEES,
            ROUTE_IDS.DATA_PERFORMANCE_FEES,
            ROUTE_IDS.DATA_SETTLEMENT_INSTRUCTIONS,
            ROUTE_IDS.DATA_SETTLEMENT_FAILURES,
            ROUTE_IDS.DATA_CLIENTS,
            ROUTE_IDS.DATA_PORTFOLIOS,
            ROUTE_IDS.DATA_POSITIONS,
            ROUTE_IDS.DATA_JOURNALS,
            ROUTE_IDS.DATA_FISCAL_PERIODS,
            ROUTE_IDS.DATA_PERFORMANCE,
            ROUTE_IDS.DATA_TAX_RECORDS,
            ROUTE_IDS.DATA_RISK_EXPOSURES,
            ROUTE_IDS.DATA_COMPLIANCE_BREACHES,
            ROUTE_IDS.PLATFORM_ORGANIZATIONS_LIST,
            ROUTE_IDS.PLATFORM_USERS_LIST,
            ROUTE_IDS.PLATFORM_AUDIT_LIST,
            ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
            ROUTE_IDS.PLATFORM_MODULES_LIST,
            ROUTE_IDS.ORGANIZATION_BRANCHES_LIST,
            ROUTE_IDS.ORGANIZATION_MODULES_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENTS_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
            ROUTE_IDS.ORGANIZATION_PORTFOLIOS_LIST,
            ROUTE_IDS.TRANSACTIONS,
            ROUTE_IDS.TRANSACTION_CREATE,
            ROUTE_IDS.TRANSACTION_APPROVALS,
            ROUTE_IDS.TRANSACTION_REVERSALS,
            ROUTE_IDS.ORGANIZATION_USERS_LIST,
            ROUTE_IDS.ORGANIZATION_ROLES_LIST,
            ROUTE_IDS.ORGANIZATION_PROFILE,
            ROUTE_IDS.ORGANIZATION_AUDIT_LIST,
            ROUTE_IDS.PLATFORM_ACCESS_INSPECTOR,
            ROUTE_IDS.ORGANIZATION_ACCESS_INSPECTOR,
            ROUTE_IDS.SETTINGS_MFA,
        ]),
        description: 'Platform user with limited platform-scope permissions (health, audit view).',
    },
    {
        id: 'org-admin',
        label: 'Org Admin',
        tier: 'ORG_ADMIN',
        aal: 'AAL2',
        bundleIds: ['organization.admin'],
        enabledModules: ALL_MODULES,
        actorType: 'USER',
        expectedRoutes: makeRouteExpectations([
            ...COMMON_VISIBLE_ROUTES,
            ROUTE_IDS.REPORTS,
            ROUTE_IDS.ADMIN_HUB,
            ROUTE_IDS.DATA_HUB,
            ROUTE_IDS.DATA_INSTRUMENTS,
            ROUTE_IDS.TRANSACTIONS,
            ROUTE_IDS.TRANSACTION_CREATE,
            ROUTE_IDS.TRANSACTION_APPROVALS,
            ROUTE_IDS.TRANSACTION_REVERSALS,
            ROUTE_IDS.DATA_IMPORTS,
            ROUTE_IDS.DATA_CORPORATE_ACTIONS,
            ROUTE_IDS.DATA_RECONCILIATION,
            ROUTE_IDS.DATA_FACT_BATCHES,
            ROUTE_IDS.DATA_HOLDINGS,
            ROUTE_IDS.DATA_CASH,
            ROUTE_IDS.DATA_POSITION_TRANSFERS,
            ROUTE_IDS.DATA_MANAGEMENT_FEES,
            ROUTE_IDS.DATA_PERFORMANCE_FEES,
            ROUTE_IDS.DATA_SETTLEMENT_INSTRUCTIONS,
            ROUTE_IDS.DATA_SETTLEMENT_FAILURES,
            ROUTE_IDS.DATA_CLIENTS,
            ROUTE_IDS.DATA_PORTFOLIOS,
            ROUTE_IDS.DATA_POSITIONS,
            ROUTE_IDS.DATA_JOURNALS,
            ROUTE_IDS.DATA_FISCAL_PERIODS,
            ROUTE_IDS.DATA_PERFORMANCE,
            ROUTE_IDS.DATA_TAX_RECORDS,
            ROUTE_IDS.DATA_RISK_EXPOSURES,
            ROUTE_IDS.DATA_COMPLIANCE_BREACHES,
            ROUTE_IDS.HOLDINGS,
            ROUTE_IDS.ORGANIZATION_USERS_LIST,
            ROUTE_IDS.ORGANIZATION_ROLES_LIST,
            ROUTE_IDS.ORGANIZATION_PROFILE,
            ROUTE_IDS.ORGANIZATION_AUDIT_LIST,
            ROUTE_IDS.ORGANIZATION_BRANCHES_LIST,
            ROUTE_IDS.ORGANIZATION_MODULES_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENTS_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
            ROUTE_IDS.ORGANIZATION_PORTFOLIOS_LIST,
            ROUTE_IDS.ORGANIZATION_ACCESS_INSPECTOR,
            ROUTE_IDS.ORGANIZATION_GOVERNANCE_WORKSPACE,
            ROUTE_IDS.ADMIN_OPENING_CUTOVER_LIST,
            ROUTE_IDS.ADMIN_OPENING_CUTOVER_HISTORY,
            ROUTE_IDS.ADMIN_TAX_RULES_LIST,
            ROUTE_IDS.ORGANIZATION_JURISDICTION_SETTINGS,
            ROUTE_IDS.SETTINGS_PROFILE,
            ROUTE_IDS.SETTINGS_PREFERENCES,
            ROUTE_IDS.SETTINGS_MFA,
        ], [ROUTE_IDS.RECONCILIATION, ROUTE_IDS.CORPORATE_ACTIONS, ROUTE_IDS.PLATFORM_ACCESS_INSPECTOR], [
            ...COMMON_HIDDEN_ROUTES,
            ROUTE_IDS.DATA_MARKET_PRICES,
            ROUTE_IDS.PLATFORM_ORGANIZATIONS_LIST,
            ROUTE_IDS.PLATFORM_USERS_LIST,
            ROUTE_IDS.PLATFORM_AUDIT_LIST,
            ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
            ROUTE_IDS.PLATFORM_HEALTH,
            ROUTE_IDS.PLATFORM_MODULES_LIST,
        ]),
        description: 'Organization admin with investment and accounting permissions. Has Admin hub access.',
    },
    {
        id: 'org-user',
        label: 'Org User',
        tier: 'ORG_USER',
        aal: 'AAL1',
        bundleIds: ['organization.user.base'],
        enabledModules: ['INVESTMENT', 'REPORTING'],
        actorType: 'USER',
        expectedRoutes: makeRouteExpectations([
            ...COMMON_VISIBLE_ROUTES,
            ROUTE_IDS.REPORTS,
            ROUTE_IDS.TRANSACTIONS,
            ROUTE_IDS.TRANSACTION_CREATE,
            ROUTE_IDS.SETTINGS_PROFILE,
            ROUTE_IDS.SETTINGS_PREFERENCES,
        ], [ROUTE_IDS.HOLDINGS, ROUTE_IDS.RECONCILIATION, ROUTE_IDS.CORPORATE_ACTIONS], [
            ...COMMON_HIDDEN_ROUTES,
            ROUTE_IDS.ADMIN_HUB,
            ROUTE_IDS.ORGANIZATION_USERS_LIST,
            ROUTE_IDS.ORGANIZATION_ROLES_LIST,
            ROUTE_IDS.ORGANIZATION_AUDIT_LIST,
            ROUTE_IDS.ORGANIZATION_PROFILE,
            ROUTE_IDS.ORGANIZATION_PROFILE_DETAIL,
            ROUTE_IDS.PLATFORM_ACCESS_INSPECTOR,
            ROUTE_IDS.ORGANIZATION_ACCESS_INSPECTOR,
            ROUTE_IDS.DATA_HUB,
            ROUTE_IDS.DATA_INSTRUMENTS,
            ROUTE_IDS.DATA_INSTRUMENT_DETAIL,
            ROUTE_IDS.DATA_MARKET_PRICES,
            ROUTE_IDS.DATA_IMPORTS,
            ROUTE_IDS.DATA_IMPORT_DETAIL,
            ROUTE_IDS.DATA_RECONCILIATION,
            ROUTE_IDS.DATA_RECONCILIATION_DETAIL,
            ROUTE_IDS.DATA_CORPORATE_ACTIONS,
            ROUTE_IDS.DATA_CORPORATE_ACTION_DETAIL,
            ROUTE_IDS.DATA_FACT_BATCHES,
            ROUTE_IDS.DATA_FACT_BATCH_DETAIL,
            ROUTE_IDS.DATA_HOLDINGS,
            ROUTE_IDS.DATA_CASH,
            ROUTE_IDS.DATA_POSITION_TRANSFERS,
            ROUTE_IDS.DATA_MANAGEMENT_FEES,
            ROUTE_IDS.DATA_PERFORMANCE_FEES,
            ROUTE_IDS.DATA_SETTLEMENT_INSTRUCTIONS,
            ROUTE_IDS.DATA_SETTLEMENT_FAILURES,
            ROUTE_IDS.DATA_CLIENTS,
            ROUTE_IDS.DATA_PORTFOLIOS,
            ROUTE_IDS.DATA_POSITIONS,
            ROUTE_IDS.DATA_JOURNALS,
            ROUTE_IDS.DATA_FISCAL_PERIODS,
            ROUTE_IDS.DATA_PERFORMANCE,
            ROUTE_IDS.DATA_TAX_RECORDS,
            ROUTE_IDS.DATA_RISK_EXPOSURES,
            ROUTE_IDS.DATA_COMPLIANCE_BREACHES,
            ROUTE_IDS.PLATFORM_ORGANIZATIONS_LIST,
            ROUTE_IDS.PLATFORM_USERS_LIST,
            ROUTE_IDS.PLATFORM_AUDIT_LIST,
            ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
            ROUTE_IDS.PLATFORM_HEALTH,
            ROUTE_IDS.PLATFORM_MODULES_LIST,
            ROUTE_IDS.ORGANIZATION_BRANCHES_LIST,
            ROUTE_IDS.ORGANIZATION_MODULES_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENTS_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
            ROUTE_IDS.ORGANIZATION_PORTFOLIOS_LIST,
            ROUTE_IDS.TRANSACTION_APPROVALS,
            ROUTE_IDS.TRANSACTION_REVERSALS,
            ROUTE_IDS.SETTINGS_MFA,
        ]),
        description: 'Regular org user with read/write transaction access and read-only investment access.',
    },
    {
        id: 'org-client',
        label: 'Org Client',
        tier: 'ORG_CLIENT',
        aal: 'AAL1',
        bundleIds: ['organization.client.base'],
        enabledModules: ['INVESTMENT', 'REPORTING'],
        actorType: 'CLIENT',
        expectedRoutes: makeRouteExpectations([
            ...COMMON_VISIBLE_ROUTES,
            ROUTE_IDS.REPORTS,
            ROUTE_IDS.TRANSACTIONS,
            ROUTE_IDS.SETTINGS_PROFILE,
            ROUTE_IDS.SETTINGS_PREFERENCES,
        ], [ROUTE_IDS.HOLDINGS, ROUTE_IDS.RECONCILIATION, ROUTE_IDS.CORPORATE_ACTIONS], [
            ...COMMON_HIDDEN_ROUTES,
            ROUTE_IDS.ADMIN_HUB,
            ROUTE_IDS.ORGANIZATION_USERS_LIST,
            ROUTE_IDS.ORGANIZATION_ROLES_LIST,
            ROUTE_IDS.ORGANIZATION_AUDIT_LIST,
            ROUTE_IDS.ORGANIZATION_PROFILE,
            ROUTE_IDS.ORGANIZATION_PROFILE_DETAIL,
            ROUTE_IDS.PLATFORM_ACCESS_INSPECTOR,
            ROUTE_IDS.ORGANIZATION_ACCESS_INSPECTOR,
            ROUTE_IDS.DATA_HUB,
            ROUTE_IDS.DATA_INSTRUMENTS,
            ROUTE_IDS.DATA_INSTRUMENT_DETAIL,
            ROUTE_IDS.DATA_MARKET_PRICES,
            ROUTE_IDS.DATA_IMPORTS,
            ROUTE_IDS.DATA_IMPORT_DETAIL,
            ROUTE_IDS.DATA_RECONCILIATION,
            ROUTE_IDS.DATA_RECONCILIATION_DETAIL,
            ROUTE_IDS.DATA_CORPORATE_ACTIONS,
            ROUTE_IDS.DATA_CORPORATE_ACTION_DETAIL,
            ROUTE_IDS.DATA_FACT_BATCHES,
            ROUTE_IDS.DATA_FACT_BATCH_DETAIL,
            ROUTE_IDS.DATA_HOLDINGS,
            ROUTE_IDS.DATA_CASH,
            ROUTE_IDS.DATA_POSITION_TRANSFERS,
            ROUTE_IDS.DATA_MANAGEMENT_FEES,
            ROUTE_IDS.DATA_PERFORMANCE_FEES,
            ROUTE_IDS.DATA_SETTLEMENT_INSTRUCTIONS,
            ROUTE_IDS.DATA_SETTLEMENT_FAILURES,
            ROUTE_IDS.DATA_CLIENTS,
            ROUTE_IDS.DATA_PORTFOLIOS,
            ROUTE_IDS.DATA_POSITIONS,
            ROUTE_IDS.DATA_JOURNALS,
            ROUTE_IDS.DATA_FISCAL_PERIODS,
            ROUTE_IDS.DATA_PERFORMANCE,
            ROUTE_IDS.DATA_TAX_RECORDS,
            ROUTE_IDS.DATA_RISK_EXPOSURES,
            ROUTE_IDS.DATA_COMPLIANCE_BREACHES,
            ROUTE_IDS.PLATFORM_ORGANIZATIONS_LIST,
            ROUTE_IDS.PLATFORM_USERS_LIST,
            ROUTE_IDS.PLATFORM_AUDIT_LIST,
            ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
            ROUTE_IDS.PLATFORM_HEALTH,
            ROUTE_IDS.PLATFORM_MODULES_LIST,
            ROUTE_IDS.ORGANIZATION_BRANCHES_LIST,
            ROUTE_IDS.ORGANIZATION_MODULES_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENTS_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
            ROUTE_IDS.ORGANIZATION_PORTFOLIOS_LIST,
            ROUTE_IDS.TRANSACTION_CREATE,
            ROUTE_IDS.TRANSACTION_APPROVALS,
            ROUTE_IDS.TRANSACTION_REVERSALS,
            ROUTE_IDS.SETTINGS_MFA,
        ]),
        description: 'Client user with read-only transaction visibility, no create access.',
    },
    {
        id: 'support-delegate',
        label: 'Support Delegate',
        tier: 'PLATFORM_ADMIN',
        aal: 'AAL2',
        bundleIds: ['platform.admin'],
        enabledModules: ['PLATFORM', 'AUTH', 'DASHBOARD', 'SETTINGS', 'NAVIGATION'],
        actorType: 'SUPPORT_DELEGATE',
        delegationContext: {
            mode: 'SUPPORT_READ_ONLY',
            delegationId: 'delegation-support-001',
            targetBranchId: 'branch-1',
            allowedPermissions: SUPPORT_DELEGATE_PERMISSIONS,
            expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            sourcePersona: 'PLATFORM_ADMIN',
        },
        expectedRoutes: makeRouteExpectations([
            ...COMMON_VISIBLE_ROUTES,
            ROUTE_IDS.REPORTS,
            ROUTE_IDS.DATA_HUB,
            ROUTE_IDS.DATA_INSTRUMENTS,
            ROUTE_IDS.TRANSACTIONS,
            ROUTE_IDS.DATA_RECONCILIATION,
            ROUTE_IDS.DATA_FACT_BATCHES,
            ROUTE_IDS.SETTINGS_PROFILE,
            ROUTE_IDS.SETTINGS_PREFERENCES,
        ], [
            ROUTE_IDS.HOLDINGS,
            ROUTE_IDS.RECONCILIATION,
            ROUTE_IDS.CORPORATE_ACTIONS,
            ROUTE_IDS.DATA_HOLDINGS,
            ROUTE_IDS.DATA_CASH,
            ROUTE_IDS.DATA_POSITION_TRANSFERS,
            ROUTE_IDS.DATA_MANAGEMENT_FEES,
            ROUTE_IDS.DATA_PERFORMANCE_FEES,
            ROUTE_IDS.DATA_SETTLEMENT_INSTRUCTIONS,
            ROUTE_IDS.DATA_SETTLEMENT_FAILURES,
        ], [
            ...COMMON_HIDDEN_ROUTES,
            ROUTE_IDS.ADMIN_HUB,
            ROUTE_IDS.ORGANIZATION_USERS_LIST,
            ROUTE_IDS.ORGANIZATION_ROLES_LIST,
            ROUTE_IDS.ORGANIZATION_AUDIT_LIST,
            ROUTE_IDS.ORGANIZATION_PROFILE,
            ROUTE_IDS.ORGANIZATION_PROFILE_DETAIL,
            ROUTE_IDS.PLATFORM_ACCESS_INSPECTOR,
            ROUTE_IDS.ORGANIZATION_ACCESS_INSPECTOR,
            ROUTE_IDS.TRANSACTION_CREATE,
            ROUTE_IDS.DATA_MARKET_PRICES,
            ROUTE_IDS.DATA_IMPORTS,
            ROUTE_IDS.DATA_IMPORT_DETAIL,
            ROUTE_IDS.DATA_CORPORATE_ACTIONS,
            ROUTE_IDS.PLATFORM_ORGANIZATIONS_LIST,
            ROUTE_IDS.PLATFORM_USERS_LIST,
            ROUTE_IDS.PLATFORM_AUDIT_LIST,
            ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
            ROUTE_IDS.PLATFORM_HEALTH,
            ROUTE_IDS.PLATFORM_MODULES_LIST,
            ROUTE_IDS.ORGANIZATION_BRANCHES_LIST,
            ROUTE_IDS.ORGANIZATION_MODULES_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENTS_LIST,
            ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
            ROUTE_IDS.ORGANIZATION_PORTFOLIOS_LIST,
            ROUTE_IDS.DATA_CLIENTS,
            ROUTE_IDS.DATA_PORTFOLIOS,
            ROUTE_IDS.DATA_POSITIONS,
            ROUTE_IDS.DATA_JOURNALS,
            ROUTE_IDS.DATA_FISCAL_PERIODS,
            ROUTE_IDS.DATA_PERFORMANCE,
            ROUTE_IDS.DATA_TAX_RECORDS,
            ROUTE_IDS.DATA_RISK_EXPOSURES,
            ROUTE_IDS.DATA_COMPLIANCE_BREACHES,
            ROUTE_IDS.SETTINGS_MFA,
        ]),
        description: 'Support delegate with read-only access across modules via delegation context. Uses SUPPORT_DELEGATE actor type.',
    },
    {
        id: 'anonymous',
        label: 'Anonymous (Unauthenticated)',
        tier: 'ORG_USER',
        aal: 'AAL1',
        bundleIds: [],
        enabledModules: [],
        actorType: 'USER',
        expectedRoutes: makeRouteExpectations([ROUTE_IDS.LOGIN, ROUTE_IDS.FORGOT_PASSWORD, ROUTE_IDS.RESET_PASSWORD], [], [
            ROUTE_IDS.DASHBOARD,
            ROUTE_IDS.TRANSACTIONS,
            ROUTE_IDS.TRANSACTION_CREATE,
            ROUTE_IDS.TRANSACTION_DETAIL,
            ROUTE_IDS.SETTINGS,
            ROUTE_IDS.SETTINGS_PROFILE,
            ROUTE_IDS.SETTINGS_PREFERENCES,
            ROUTE_IDS.SETTINGS_MFA,
            ROUTE_IDS.DATA_HUB,
            ROUTE_IDS.ADMIN_HUB,
        ]),
        description: 'Unauthenticated user — only auth routes visible, all protected routes redirect to login.',
    },
];
export function getPersonaById(id) {
    return PERSONA_CASES.find((p) => p.id === id);
}
export function getAuthenticatedPersonas() {
    return PERSONA_CASES.filter((p) => p.id !== 'anonymous');
}
export function getPersonaExpectedRoutes(personaId) {
    const persona = getPersonaById(personaId);
    return persona?.expectedRoutes;
}
export function getPersonaExpectedAvailableRoutes(personaId) {
    const persona = getPersonaById(personaId);
    if (!persona)
        return [];
    return Object.entries(persona.expectedRoutes)
        .filter(([, exp]) => exp.state === 'AVAILABLE')
        .map(([route]) => route);
}
export function getPersonaExpectedHiddenRoutes(personaId) {
    const persona = getPersonaById(personaId);
    if (!persona)
        return [];
    return Object.entries(persona.expectedRoutes)
        .filter(([, exp]) => exp.state === 'HIDDEN')
        .map(([route]) => route);
}
export function getPersonaExpectedLockedRoutes(personaId) {
    const persona = getPersonaById(personaId);
    if (!persona)
        return [];
    return Object.entries(persona.expectedRoutes)
        .filter(([, exp]) => exp.state !== 'AVAILABLE' && exp.state !== 'HIDDEN')
        .map(([route]) => route);
}
